import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { Badge, Button, Dialog, Field, Textarea, useToast } from './ui';

interface ImportRow {
  line: number;
  name: string;
  action: 'create' | 'update' | 'unchanged';
  role: string | null;
  membership: boolean;
}
interface ImportResult {
  dryRun: boolean;
  rows: ImportRow[];
  created: number;
  updated: number;
  problems: { path: string; message: string }[];
}

const ACTION_LABELS: Record<ImportRow['action'], string> = {
  create: 'Neu',
  update: 'Ändert sich',
  unchanged: 'Unverändert',
};

const BEISPIEL = [
  'Vorname;Nachname;E-Mail;Telefon;Funktion;Bereich;Eintritt;Beitrag',
  'Erika;Musterfrau;erika@example.at;+43 664 1234567;Trainer;Tennis;2021-03-01;96,50',
].join('\n');

/**
 * Personenimport aus einer Tabelle.
 *
 * Zwei Schritte, bewusst getrennt: erst pruefen, dann uebernehmen. Der
 * Uebernehmen-Knopf erscheint erst, wenn keine Zeile mehr beanstandet wird -
 * ein halb eingelesener Mitgliederstand waere schlimmer als gar keiner.
 */
export function PersonImportDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const [csv, setCsv] = useState('');
  const [bericht, setBericht] = useState<ImportResult | null>(null);

  const fehler = (fallback: string) => (error: unknown) =>
    toast.show(error instanceof ApiError ? error.message : fallback, 'error');

  const lauf = useMutation({
    mutationFn: (dryRun: boolean) =>
      api.post<ImportResult>('/api/persons/import', { csv, dryRun }),
    onSuccess: async (result) => {
      setBericht(result);
      if (!result.dryRun) {
        await qc.invalidateQueries({ queryKey: ['persons'] });
        await qc.invalidateQueries({ queryKey: ['members'] });
        toast.show(`${result.created} angelegt, ${result.updated} geändert`);
        onClose();
        setCsv('');
        setBericht(null);
      }
    },
    onError: fehler('Import fehlgeschlagen'),
  });

  const dateiLesen = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    setCsv(await file.text());
    setBericht(null);
  };

  const sauber = bericht !== null && bericht.problems.length === 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Personen importieren"
      wide
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            variant="secondary"
            loading={lauf.isPending}
            disabled={!csv.trim()}
            onClick={() => lauf.mutate(true)}
          >
            Prüfen
          </Button>
          {sauber && (
            <Button loading={lauf.isPending} onClick={() => lauf.mutate(false)}>
              {bericht.created + bericht.updated} übernehmen
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          Pflichtspalten sind <strong>Vorname</strong> und <strong>Nachname</strong>. Optional:
          E-Mail, Telefon, Funktion und Bereich (nur gemeinsam), sowie Eintritt, Geburtsdatum und
          Beitrag für die Mitgliedschaft. Datumsangaben als JJJJ-MM-TT. Bereits vorhandene
          Personen werden an der E-Mail erkannt, sonst am Namen.
        </p>

        <Field label="Datei auswählen" hint="CSV mit Semikolon oder Komma">
          {(id) => (
            <input
              id={id}
              type="file"
              accept=".csv,text/csv,text/plain"
              className="block w-full text-sm"
              onChange={(e) => void dateiLesen(e.target.files?.[0])}
            />
          )}
        </Field>

        <Field label="Oder Inhalt einfügen">
          {(id) => (
            <Textarea
              id={id}
              rows={6}
              value={csv}
              placeholder={BEISPIEL}
              onChange={(e) => {
                setCsv(e.target.value);
                setBericht(null);
              }}
            />
          )}
        </Field>

        {bericht && (
          <div className="space-y-2">
            <p className="flex flex-wrap gap-2">
              <Badge tone="success">{bericht.created} neu</Badge>
              <Badge tone="info">{bericht.updated} ändern sich</Badge>
              {bericht.problems.length > 0 && (
                <Badge tone="danger">{bericht.problems.length} zu klären</Badge>
              )}
            </p>

            {bericht.problems.length > 0 && (
              <ul className="space-y-1 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">
                {bericht.problems.map((problem) => (
                  <li key={`${problem.path}-${problem.message}`}>
                    <strong>{problem.path}:</strong> {problem.message}
                  </li>
                ))}
              </ul>
            )}

            {bericht.rows.length > 0 && (
              <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200">
                <table className="w-full text-sm">
                  <tbody className="divide-y divide-slate-100">
                    {bericht.rows.map((row) => (
                      <tr key={row.line}>
                        <td className="px-3 py-1.5 text-slate-400">{row.line}</td>
                        <td className="px-3 py-1.5">{row.name}</td>
                        <td className="px-3 py-1.5 text-slate-500">
                          {[row.role, row.membership ? 'Mitgliedschaft' : null]
                            .filter(Boolean)
                            .join(' · ')}
                        </td>
                        <td className="px-3 py-1.5 text-right">
                          <Badge tone={row.action === 'create' ? 'success' : 'neutral'}>
                            {ACTION_LABELS[row.action]}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </Dialog>
  );
}
